import {
  BoxGeometry,
  BufferAttribute,
  CapsuleGeometry,
  CylinderGeometry,
  Euler,
  ExtrudeGeometry,
  LatheGeometry,
  Matrix4,
  Quaternion,
  Shape,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3,
  type BufferGeometry,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * 部品の仕上げ（材質の種類）。同じ仕上げの部品は、1 両ごとに 1 つの形状にまとめて描く（描画の負荷を抑える）。
 *
 * - `paint`：塗装（つやのある車体・金属）
 * - `matte`：つやのない面（布・肌・ゴム・木）
 * - `glass`：窓（暗く、強く反射する）
 * - `light`：ライト（照明の影響を受けず、明るく光る）
 */
export type Finish = 'paint' | 'matte' | 'glass' | 'light';

/** 3 次元の点（車両の局所座標：X = 右、Y = 上、Z = 後方。前方は −Z）。 */
export type P3 = readonly [number, number, number];

interface Part {
  readonly geometry: BufferGeometry;
  readonly color: number;
  readonly finish: Finish;
}

/** 部品の置き方：位置・回転（オイラー角 [rad]）・拡大。 */
export interface Placement {
  readonly position?: P3;
  readonly rotation?: P3;
  readonly scale?: P3;
}

const UP = new Vector3(0, 1, 0);

/**
 * 車両 1 両分の部品の集まり。形状に置き方を焼き込んで、仕上げごとに 1 つの形状へまとめる。
 * 部品の形状は、まとめた後に解放する。
 */
export class PartList {
  private readonly parts: Part[] = [];

  /** 形状を、置き方を焼き込んで加える。 */
  add(geometry: BufferGeometry, color: number, finish: Finish, placement: Placement = {}): void {
    const matrix = new Matrix4().compose(
      new Vector3(...(placement.position ?? [0, 0, 0])),
      new Quaternion().setFromEuler(new Euler(...(placement.rotation ?? [0, 0, 0]))),
      new Vector3(...(placement.scale ?? [1, 1, 1])),
    );
    geometry.applyMatrix4(matrix);
    this.parts.push({ geometry, color, finish });
  }

  /** 直方体（幅・高さ・奥行き）。 */
  box(size: P3, color: number, finish: Finish, placement: Placement = {}): void {
    this.add(new BoxGeometry(size[0], size[1], size[2]), color, finish, placement);
  }

  /** 球（`scale` で楕円体にできる）。 */
  sphere(
    radius: number,
    color: number,
    finish: Finish,
    placement: Placement = {},
    segments: readonly [number, number] = [12, 8],
  ): void {
    this.add(new SphereGeometry(radius, segments[0], segments[1]), color, finish, placement);
  }

  /** 円柱。`axis` の向きに置く（`x` は左右、`z` は前後、`y` は上下）。 */
  cylinder(
    radiusTop: number,
    radiusBottom: number,
    length: number,
    axis: 'x' | 'y' | 'z',
    color: number,
    finish: Finish,
    position: P3,
    segments = 18,
  ): void {
    const rotation: P3 =
      axis === 'x' ? [0, 0, Math.PI / 2] : axis === 'z' ? [Math.PI / 2, 0, 0] : [0, 0, 0];
    this.add(new CylinderGeometry(radiusTop, radiusBottom, length, segments), color, finish, {
      position,
      rotation,
    });
  }

  /** 2 点を結ぶ円柱（パイプ・フレーム・ロッド）。 */
  tube(a: P3, b: P3, radius: number, color: number, finish: Finish, segments = 8): void {
    this.between(new CylinderGeometry(radius, radius, 1, segments), a, b, color, finish, true);
  }

  /** 2 点を結ぶカプセル（人の手足）。 */
  capsule(a: P3, b: P3, radius: number, color: number, finish: Finish): void {
    const length = Math.max(1e-3, distance(a, b));
    this.between(new CapsuleGeometry(radius, length, 1, 6), a, b, color, finish, false);
  }

  /** 円環（タイヤ・ハンドル）。軸は `axis` の向き。 */
  torus(
    radius: number,
    tube: number,
    axis: 'x' | 'y' | 'z',
    color: number,
    finish: Finish,
    position: P3,
    segments: readonly [number, number] = [8, 24],
  ): void {
    const rotation: P3 =
      axis === 'x' ? [0, Math.PI / 2, 0] : axis === 'y' ? [Math.PI / 2, 0, 0] : [0, 0, 0];
    this.add(new TorusGeometry(radius, tube, segments[0], segments[1]), color, finish, {
      position,
      rotation,
    });
  }

  /**
   * 回転体（煙突・ドーム・鼻先など）。`profile` は [半径, 高さ] の列で、Y 軸のまわりに回す。
   * `placement.rotation` で向きを変えられる。
   */
  lathe(
    profile: readonly (readonly [number, number])[],
    color: number,
    finish: Finish,
    placement: Placement = {},
    segments = 20,
  ): void {
    const points = profile.map(([r, y]) => new Vector2(Math.max(0, r), y));
    this.add(new LatheGeometry(points, segments), color, finish, placement);
  }

  /**
   * 横から見た輪郭を、幅の方向に押し出した車体（角は丸める）。
   * 輪郭は [前方への距離 s（正が前）, 高さ y] の列（閉じた多角形。曲線は細かい点で表す）。
   * 中心は X = `centerX`、幅は `width`（丸めた角を含む）。
   */
  side(
    profile: readonly (readonly [number, number])[],
    width: number,
    color: number,
    finish: Finish,
    options: { readonly bevel?: number; readonly centerX?: number } = {},
  ): void {
    const bevel = Math.min(options.bevel ?? 0.05, width / 4);
    const shape = new Shape(profile.map(([s, y]) => new Vector2(s, y)));
    const geometry = new ExtrudeGeometry(shape, {
      depth: Math.max(1e-3, width - 2 * bevel),
      bevelEnabled: bevel > 0,
      bevelThickness: bevel,
      bevelSize: bevel,
      // 角を丸めても、輪郭（横から見た形）が大きくならないように、内側へずらしてから丸める
      bevelOffset: -bevel,
      bevelSegments: 3,
      curveSegments: 8,
    });
    // 輪郭の x（前方 s）を −Z へ、押し出しの方向（z）を X へ向ける
    geometry.rotateY(Math.PI / 2);
    geometry.translate(-(width - 2 * bevel) / 2 + (options.centerX ?? 0), 0, 0);
    this.add(geometry, color, finish);
  }

  /**
   * 断面（[x, y] の列。正面から見た形）を、前後の方向に `length` だけ押し出す（客車の丸屋根など）。
   * 前後の中心は Z = `centerZ`。
   */
  section(
    profile: readonly (readonly [number, number])[],
    length: number,
    color: number,
    finish: Finish,
    centerZ = 0,
  ): void {
    const shape = new Shape(profile.map(([x, y]) => new Vector2(x, y)));
    const geometry = new ExtrudeGeometry(shape, {
      depth: length,
      bevelEnabled: false,
      curveSegments: 8,
    });
    geometry.translate(0, 0, centerZ - length / 2);
    this.add(geometry, color, finish);
  }

  /** 部品の数（テスト用）。 */
  get count(): number {
    return this.parts.length;
  }

  /**
   * 仕上げごとに、部品を 1 つの形状へまとめる（頂点の色で部品の色を持つ）。部品の形状は解放する。
   * 部品のない仕上げは含めない。
   */
  merge(): Map<Finish, BufferGeometry> {
    const groups = new Map<Finish, BufferGeometry[]>();
    for (const part of this.parts) {
      const flat = part.geometry;
      // まとめるために、属性をそろえる（位置・法線・頂点の色）。頂点を共有する形（球など）は、
      // 索引付きのまま残して頂点の数を抑える。索引のない形（押し出し）には、そのままの順の索引を付ける
      for (const name of Object.keys(flat.attributes)) {
        if (name !== 'position' && name !== 'normal') flat.deleteAttribute(name);
      }
      flat.clearGroups();
      const count = flat.getAttribute('position').count;
      if (!flat.index) flat.setIndex(Array.from({ length: count }, (_, i) => i));
      const colors = new Float32Array(count * 3);
      const r = ((part.color >> 16) & 0xff) / 255;
      const g = ((part.color >> 8) & 0xff) / 255;
      const b = (part.color & 0xff) / 255;
      for (let i = 0; i < count; i += 1) {
        colors[i * 3] = r;
        colors[i * 3 + 1] = g;
        colors[i * 3 + 2] = b;
      }
      flat.setAttribute('color', new BufferAttribute(colors, 3));
      const list = groups.get(part.finish) ?? [];
      list.push(flat);
      groups.set(part.finish, list);
    }
    const merged = new Map<Finish, BufferGeometry>();
    for (const [finish, list] of groups) {
      const geometry = mergeGeometries(list, false);
      for (const g of list) g.dispose();
      if (geometry) {
        geometry.computeBoundingSphere();
        merged.set(finish, geometry);
      }
    }
    this.parts.length = 0;
    return merged;
  }

  private between(
    geometry: BufferGeometry,
    a: P3,
    b: P3,
    color: number,
    finish: Finish,
    stretch: boolean,
  ): void {
    const from = new Vector3(...a);
    const to = new Vector3(...b);
    const direction = to.clone().sub(from);
    const length = Math.max(1e-3, direction.length());
    const rotation = new Quaternion().setFromUnitVectors(UP, direction.normalize());
    const matrix = new Matrix4().compose(
      from.clone().add(to).multiplyScalar(0.5),
      rotation,
      new Vector3(1, stretch ? length : 1, 1),
    );
    geometry.applyMatrix4(matrix);
    this.parts.push({ geometry, color, finish });
  }
}

/** 2 点の距離。 */
export function distance(a: P3, b: P3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/** 2 点の和。 */
export function plus(a: P3, b: P3): P3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

/**
 * 滑らかな曲線を、折れ線の点で表す（2 次ベジェ曲線）。`from` から `control` の方へ曲がって `to` へ。
 * 両端を含めて `steps + 1` 点。
 */
export function curve(
  from: readonly [number, number],
  control: readonly [number, number],
  to: readonly [number, number],
  steps = 8,
): [number, number][] {
  const points: [number, number][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const u = 1 - t;
    points.push([
      u * u * from[0] + 2 * u * t * control[0] + t * t * to[0],
      u * u * from[1] + 2 * u * t * control[1] + t * t * to[1],
    ]);
  }
  return points;
}
